import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BadgeColour, ComponentsModule, DialogService } from '@mastechnicaegroup/carelinelive-ui';
import { ActivityService } from '@lib/activity/activity.service';
import { Activity } from '@lib/api/models/activity';
import { PaginatedApiResponse, Meta } from '@lib/api/responses/paginated-api-response';
import { AppDatePipe } from '@shared/pipes/date.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, switchMap, map } from 'rxjs';
import { ViewCommissionerAuditEntryDialogComponent } from '../../../../dialogs/commissioner/view/audit/view-commissioner-audit-entry-dialog.component';

interface ChangedField {
    key: string;
    label: string;
    newValue: string;
}

@Component({
    selector: 'app-commissioner-audit-tab',
    imports: [
        CommonModule,
        ComponentsModule,
        AppDatePipe,
    ],
    templateUrl: './commissioner-audit-tab.component.html',
    standalone: true,
    styleUrl: './commissioner-audit-tab.component.scss'
})
export class CommissionerAuditTabComponent {

    private readonly _activityService = inject(ActivityService);
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _dialog = inject(DialogService);

    private readonly _page$ = new BehaviorSubject<number>(
        Number(this._route.snapshot.queryParams['auditPage'] ?? 1)
    );

    protected readonly activities = signal<Activity[]>([]);
    protected readonly meta = signal<Meta | null>(null);
    protected readonly isLoading = signal(true);
    protected readonly error = signal<string | null>(null);

    protected readonly currentPage = computed(() => this.meta()?.current_page ?? 1);
    protected readonly lastPage = computed(() => this.meta()?.last_page ?? 1);
    protected readonly hasPrev = computed(() => this.currentPage() > 1);
    protected readonly hasNext = computed(() => this.currentPage() < this.lastPage());

    constructor() {
        this._page$.pipe(
            switchMap(page =>
                this._route.params.pipe(
                    map(params => params['id'] as string),
                    switchMap(commissionerId =>
                        this._activityService.getActivity('commissioner', commissionerId, page)
                    )
                )
            ),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe({
            next: (response: PaginatedApiResponse<Activity>) => {
                this.activities.set(response.data);
                this.meta.set(response.meta);
                this.isLoading.set(false);
            },
            error: () => {
                this.error.set('Failed to load audit log.');
                this.isLoading.set(false);
            }
        });
    }

    protected eventBadgeColour(event: string | null): BadgeColour {
        switch (event) {
            case 'created':
            case 'user_assigned':     return 'success';
            case 'updated':
            case 'user_role_updated': return 'primary';
            case 'deleted':
            case 'user_removed':      return 'error';
            default:                  return 'variant';
        }
    }

    protected eventLabel(event: string | null): string {
        switch (event) {
            case 'created':           return 'CREATED';
            case 'updated':           return 'UPDATED';
            case 'deleted':           return 'DELETED';
            case 'user_assigned':     return 'USER ASSIGNED';
            case 'user_removed':      return 'USER REMOVED';
            case 'user_role_updated': return 'ROLE UPDATED';
            default:                  return (event ?? 'UNKNOWN').replace(/_/g, ' ').toUpperCase();
        }
    }

    protected changedFields(entry: Activity): ChangedField[] {
        const attributes = entry.properties?.attributes;
        if (!attributes || typeof attributes !== 'object') {
            return [];
        }
        const [first] = Object.entries(attributes);
        if (!first) {
            return [];
        }
        const [key, value] = first;
        return [{
            key,
            label: key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
            newValue: this.formatPropertyValue(value),
        }];
    }

    private formatPropertyValue(value: unknown): string {
        if (value == null) {
            return '—';
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            const v = value as Record<string, unknown>;
            if ('number' in v && 'type' in v) {
                return `${v['number']} (${v['type']})`;
            }
            if ('start_time' in v && 'end_time' in v && 'open' in v) {
                return `${v['start_time']} – ${v['end_time']} (${v['open'] ? 'Open' : 'Closed'})`;
            }
            if ('start_time' in v && 'end_time' in v) {
                return `${v['start_time']} – ${v['end_time']}`;
            }
            if ('open' in v) {
                return v['open'] ? 'Open' : 'Closed';
            }
            return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(', ');
        }
        if (Array.isArray(value)) {
            return value.map(item => this.formatPropertyValue(item)).join(', ');
        }
        return String(value);
    }

    protected extraFieldCount(entry: Activity): number {
        const attributes = entry.properties?.attributes;
        if (!attributes || typeof attributes !== 'object') {
            return 0;
        }
        return Math.max(0, Object.keys(attributes).length - 1);
    }

    protected openEntry(entry: Activity): void {
        ViewCommissionerAuditEntryDialogComponent.show(this._dialog, entry);
    }

    protected goToPage(page: number): void {
        this.isLoading.set(true);
        this._page$.next(page);
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: { auditPage: page },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }
}
